/**
 * Who sees a menu item or a hub's tab (the Navigation review): the org, the viewer's rights and what the platform
 * serves them, as a plain context, so the rules are tested apart from the layout. menu.ts sorts the items into areas;
 * hubs.ts says which tabs a hub holds. An item or a tab for something the org does not have stays hidden, never shown
 * as off.
 */

/** What decides who sees an item: the org, the viewer's rights, and what the platform serves them. */
export interface MenuContext {
  /** The active org serves other plants and has none of its own (a vendor). */
  vendorOrg: boolean;
  vendorAdmin: boolean;
  platformAdmin: boolean;
  /** The foundry pack is on for the org. */
  foundry: boolean;
  /** The viewer's role grants a permission in the active org. */
  can: (perm: string) => boolean;
  /** A brief is served (or none is written yet, which Today says): briefNavVisible. */
  brief: boolean;
  /** The org has a past digest the viewer may read: digestNavVisible. */
  digests: boolean;
  /** The same rule, for studies: studyNavVisible. */
  studies: boolean;
  /** The findings are open to the viewer (knowledgeNavBadge gives no pill). */
  knowledgeOpen: boolean;
  /** The consultant answers in the org (consultantNavBadge gives no pill). */
  consultantOpen: boolean;
  /** Improvement projects are on for the org (the platform's gate and the org's switch): improvementProjectsOn. */
  projects: boolean;
  /** Today's mark: a brief written since the viewer's last visit. */
  briefMark?: string;
}

/** What an item or a tab needs before its viewer sees it. */
export interface Gate {
  /** Needs the foundry pack. */
  foundry?: boolean;
  /** Needs this permission (a write-only page, such as capture). */
  perm?: string;
  /** Needs this per-viewer flag of the context. */
  when?: "brief" | "digests" | "studies" | "knowledgeOpen" | "consultantOpen" | "projects" | "platformAdmin" | "vendorAdmin";
}

/** Whether the viewer sees what the gate guards. */
export function gateOpen(g: Gate, ctx: MenuContext): boolean {
  if (g.foundry && !ctx.foundry) return false;
  if (g.perm && !ctx.can(g.perm)) return false;
  return !g.when || ctx[g.when];
}
